import clsx from "clsx";
import { useCallback, useMemo, useRef } from "react";
import { formatColumnLabel } from "../../../helper/columnLabel";
import { clampColumnWidth, COLUMN_CONSTRAINTS } from "../../../helper/columnWidthConstraints";
import { uniqueNamedItems } from "../../../helper/uniqueNamedItems";
import { timeStampInCurrentTZ } from "../../../helper/utils";
import { StyledFilterIconSVG, StyledUpDownIconSVG } from "../../atoms/Icon/Icon.styles";
import { ColumnResizeHandle, TagLink, Td, Th, ThDiv } from "../../atoms/Table";
import { Tag } from "../../atoms/Tag/Tag";
import { useBulkContext } from "../../organisms/Provider/Bulk";
import { EllipsisMultiText, EllipsisText } from "../../organisms/Tooltip/Tooltip";
import { FilterBadgeClearable } from "../Badge";

const renderHeaderLabel = header =>
  typeof header.Header === "string" ? formatColumnLabel(header.Header) : header.render("Header");

/**
 * BulkTh
 *
 * A specialized table header cell for the Bulk Table.
 * It renders the column title and includes an interactive resizer handle.
 * Supports both drag-to-resize (via `useColumnResizeSync`) and double-click Auto-Fit.
 * Resizing is disabled for the first column (index 0).
 *
 * @param {Object} props - The component props.
 * @param {Function} [props.handleAutoFit] - Callback to trigger the auto-fit calculation for this column.
 * @param {Function} props.handleResizeStart - Callback to initiate the drag operation for resizing.
 * @param {Object} props.header - The column header metadata from `react-table`.
 * @param {number} props.index - The index of the column in the header row. Used to determine if resizing should be disabled.
 * @returns {JSX.Element} The rendered header cell `<th>` with resizing capabilities.
 */
export const BulkTh = ({ handleAutoFit, handleResizeStart, header, index, ...props }) => {
  const { cellType, className, getHeaderProps, getResizerProps, HeaderTitle, id, render } = header;

  const rtResizerProps = getResizerProps();
  const isResizerVisible = index !== 0;

  return (
    <Th
      {...getHeaderProps()}
      {...props}
      className={clsx(className, "bulk", {
        "right-align": ["amount", "qty"].includes(cellType),
        "center-align": ["input-checkbox", "status"].includes(cellType),
      })}
      data-column-id={id}
      title={formatColumnLabel(HeaderTitle)}
    >
      {/* Selection column usually has a checkbox and simpler rendering */}
      {id === "selection" ? (
        render("Header")
      ) : (
        <EllipsisText>{renderHeaderLabel(header)}</EllipsisText>
      )}

      {/* Interactive handle for resizing - hidden for the first column */}
      {isResizerVisible && (
        <ColumnResizeHandle
          columnId={id}
          ariaValueNow={Math.round(
            header.totalWidth || header.width || COLUMN_CONSTRAINTS.DEFAULT_WIDTH,
          )}
          onResizePointerDown={e => {
            rtResizerProps.onMouseDown?.(e);
            handleResizeStart(id, header);
          }}
          onAutoFit={() => handleAutoFit?.(id)}
          onKeyboardResize={delta => {
            const th = document.querySelector(`[data-column-id="${id}"]`);
            if (!th) return;
            const current = Math.round(
              th.getBoundingClientRect().width ||
                header.totalWidth ||
                COLUMN_CONSTRAINTS.DEFAULT_WIDTH,
            );
            const minW = header.minWidth || COLUMN_CONSTRAINTS.MIN_WIDTH;
            const next = clampColumnWidth(current + delta, minW);
            th.style.width = `${next}px`;
            th.style.flex = `0 0 ${next}px`;
          }}
        />
      )}
    </Th>
  );
};

/**
 * DetailTh
 *
 * A dynamic and interactive `<th>` cell used in a detail table with sorting, filtering, and resizing.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleAutoFit - Callback to trigger the auto-fit calculation for this column.
 * @param {Function} props.handleResizeStart - Handler to initiate column resize (usually stores current column state).
 * @param {Object} props.header - Header metadata object from `react-table`, including props, renderers, and config flags.
 * @param {Function} props.openFilter - Opens the filter UI for this header.
 * @param {Function} props.openSorter - Opens the sorter logic for this header.
 * @returns {JSX.Element} A styled header cell with optional sort, filter, and resizer controls.
 */
export const DetailTh = ({
  getHandleProps,
  handleAutoFit,
  handleResizeStart,
  header,
  openFilter,
  openSorter,
}) => {
  const {
    canFilter,
    canSort,
    cellType,
    className,
    filterValue,
    getHeaderProps,
    getResizerProps,
    HeaderTitle,
    id,
    isSorted,
    isSortedDesc,
    maxWidth,
    minWidth,
    render,
    setFilter,
    totalWidth,
  } = header;

  const rtResizerProps = getHandleProps ? null : getResizerProps?.();
  const resizeHandleProps = getHandleProps?.(id, {
    ariaValueNow: Math.round(totalWidth || header.width || COLUMN_CONSTRAINTS.DEFAULT_WIDTH),
  });

  // Track click timing to detect double-click and skip resize start
  const lastClickTimeRef = useRef(0);

  return (
    <Th
      {...getHeaderProps()}
      className={clsx(className, {
        "justify-right": ["amount", "qty"].includes(cellType),
        "justify-center": ["input-checkbox", "status"].includes(cellType),
      })}
      data-column-id={id}
      data-max-width={maxWidth}
      data-min-width={minWidth}
      data-total-width={totalWidth}
      title={formatColumnLabel(HeaderTitle)}
    >
      {id === "selection" ? (
        render("Header")
      ) : (
        <ThDiv>
          {canSort && (
            <StyledUpDownIconSVG
              className={clsx("for-table-header", {
                active: isSorted || isSortedDesc,
              })}
              onClick={e => {
                const cellEl = e.currentTarget.closest(`[data-column-id="${id}"]`);
                openSorter(header, cellEl);
              }}
            />
          )}
          <EllipsisText>{renderHeaderLabel(header)}</EllipsisText>
          {canFilter &&
            (filterValue?.length > 0 ? (
              <FilterBadgeClearable
                count={filterValue?.length ?? 0}
                onClickCallback={() => setFilter([])}
              />
            ) : (
              <StyledFilterIconSVG
                className="for-table-header"
                onClick={e => {
                  const cellEl = e.currentTarget.closest(`[data-column-id="${id}"]`);
                  openFilter(header, cellEl);
                }}
              />
            ))}
        </ThDiv>
      )}
      {id !== "selection" &&
        (resizeHandleProps ? (
          <ColumnResizeHandle
            {...resizeHandleProps}
            onAutoFit={() => handleAutoFit?.(id)}
          />
        ) : (
          <ColumnResizeHandle
            columnId={id}
            ariaValueNow={Math.round(totalWidth || header.width || COLUMN_CONSTRAINTS.DEFAULT_WIDTH)}
            onResizePointerDown={e => {
              const now = Date.now();
              const timeSinceLastClick = now - lastClickTimeRef.current;
              lastClickTimeRef.current = now;
              if (timeSinceLastClick < 300) return;
              rtResizerProps?.onMouseDown?.(e);
              handleResizeStart(id, header);
            }}
            onAutoFit={() => handleAutoFit?.(id)}
            onKeyboardResize={delta => {
              const th = document.querySelector(`[data-column-id="${id}"]`);
              if (!th) return;
              const current = Math.round(
                th.getBoundingClientRect().width || totalWidth || COLUMN_CONSTRAINTS.DEFAULT_WIDTH,
              );
              const minW = minWidth || COLUMN_CONSTRAINTS.MIN_WIDTH;
              const next = clampColumnWidth(current + delta, minW);
              th.style.width = `${next}px`;
              th.style.flex = `0 0 ${next}px`;
            }}
          />
        ))}
    </Th>
  );
};

export const BulkExtendedTd = ({ column, getCellProps, render, row, value, ...props }) => {
  const { setFieldErrorDetail } = useBulkContext();

  const { className, cellType, id } = column;

  const {
    original: { errors },
  } = row;

  // Map "idx" to non-field errors once
  const mapId = useMemo(() => (id === "idx" ? "non-field-errors" : id), [id]);

  // Fast guard + lookup without throwing on nullish
  const messages = useMemo(
    () => (errors && typeof errors === "object" ? errors?.[mapId] : undefined),
    [errors, mapId],
  );

  const isError = Boolean(messages);

  const isAmountOrQty = ["amount", "qty"].includes(cellType);
  const isCenterAligned = ["icon-or-text", "input-checkbox", "status"].includes(cellType);
  const isStatusCell = cellType === "status";

  // Build className once
  const tdClassName = useMemo(
    () =>
      clsx("bulk", className, {
        "center-align": isCenterAligned,
        error: isError,
        "right-align": isAmountOrQty,
      }),
    [className, isAmountOrQty, isCenterAligned, isError],
  );

  // Only pass `text` when it's a status cell; avoid passing false
  const ellipsisText = useMemo(
    () => (isStatusCell ? (value ?? "").toString() : undefined),
    [isStatusCell, value],
  );

  // Only create the handler when there is an error to show
  const showError = useCallback(() => {
    if (!isError) return;
    setFieldErrorDetail(prev => ({
      ...prev,
      open: true,
      fieldLabel: id === "idx" ? "Common" : renderHeaderLabel(column),
      messages,
    }));
  }, [isError, id, messages, column, setFieldErrorDetail]);

  return (
    <Td {...getCellProps()} {...props} className={tdClassName}>
      <EllipsisText text={ellipsisText} onClick={isError ? showError : undefined}>
        {render("Cell")}
      </EllipsisText>
    </Td>
  );
};

/**
 * A data cell (`<td>`) renderer that aligns content based on column type and handles value display.
 *
 * Params
 * - getCellProps (Function): Returns props for the current cell from `react-table`.
 * - column (Object): Column definition including className and cellType.
 * - render (Function): Renderer for cell content, typically `render("Cell")`.
 * - value (any): Raw value of the cell.
 * - ...props (any): Additional props passed to the `Td` component.
 *
 * Returns
 * - JSX.Element: A styled table cell with value rendered and ellipsized if needed.
 */
export const ExtendedTd = ({ getCellProps, column, render, value, ...props }) => {
  const { className, cellType } = column;

  const ellipsisText = useMemo(() => {
    if (cellType === "datetime") return timeStampInCurrentTZ(value).format("MMM DD, YYYY. HH:MM A");
    else if (cellType === "status") return value;
    return undefined;
  }, [cellType, value]);

  const isCheckbox = cellType === "input-checkbox";

  return (
    <Td
      {...props}
      {...getCellProps()}
      className={clsx(className, {
        "right-align": ["amount", "qty"].includes(cellType),
        "center-align": ["input-checkbox", "status"].includes(cellType),
      })}
    >
      {isCheckbox ? (
        render("Cell")
      ) : (
        <EllipsisText text={ellipsisText} shouldShowPopover={cellType === "datetime"}>
          {render("Cell")}
        </EllipsisText>
      )}
    </Td>
  );
};

/**
 * A footer cell (`<td>`) renderer that displays footer summary content with alignment.
 *
 * Params
 * - className (string): Additional class names applied to the footer cell.
 * - getFooterProps (Function): Returns props for the footer cell from `react-table`.
 * - render (Function): Renderer for footer content, typically `render("Footer")`.
 * - cellType (string): Defines how the cell should be aligned (e.g. "amount", "qty", "status").
 * - ...props (any): Additional props passed to the `Td` component.
 *
 * Returns
 * - JSX.Element: A styled footer table cell with alignment and ellipsis handling.
 */
export const ExtendedFootTd = ({ className, getFooterProps, render, cellType, ...props }) => {
  return (
    <Td
      {...getFooterProps()}
      {...props}
      className={clsx(className, "footer", {
        "right-align": ["amount", "qty"].includes(cellType),
        "center-align": ["input-checkbox", "status"].includes(cellType),
      })}
    >
      <EllipsisText>{render("Footer")}</EllipsisText>
    </Td>
  );
};

/**
 * Tagged links in a list cell, or `--` when the list is empty.
 *
 * Params
 * - items (Array): Documents shown as tags (`id` and `name`).
 * - getTo (Function): Builds the link path for one item.
 * - width (number): Ellipsis/tooltip width. Defaults to 100.
 *
 * Returns
 * - JSX.Element: Tagged links, or `--` when there are no items.
 *
 * @param {{
 *   items: ?Array<{id: *, name: string}>,
 *   getTo: function({id: *, name: string}): string,
 *   width: (number|undefined)
 * }} props
 * @return {*}
 */
export const LinkedTagListCell = ({ items, getTo, width = 100 }) => {
  const unique = uniqueNamedItems(items);
  const label = unique.length ? unique.map(item => item.name).join(", ") : "--";

  return (
    <EllipsisMultiText text={label} width={width}>
      {unique.length
        ? unique.map(item => (
            <TagLink key={item.id} to={getTo(item)} onClick={event => event.stopPropagation()}>
              <Tag className="in-table">{item.name}</Tag>
            </TagLink>
          ))
        : "--"}
    </EllipsisMultiText>
  );
};
